import { FC } from 'react'
import * as React from 'react'
import { CalendarEvent } from '../../services/calendar'
import { buildDayData, eventChipClass, HOUR_HEIGHT, longDateLabel } from './calendar-layout'

interface CalendarDayViewProps {
  date: string
  events: CalendarEvent[]
  onCreate: (date: string) => void
  onOpenEvent: (event: CalendarEvent) => void
}

/**
 * Um dia só, na mesma grade de horas da semana.
 *
 * Com uma coluna sobrando largura, cada evento mostra horário e cliente sem
 * truncar — é a visão para "o que eu faço hoje", enquanto a de semana serve
 * para enxergar os buracos da agenda.
 */
const CalendarDayView: FC<CalendarDayViewProps> = ({ date, events, onCreate, onOpenEvent }) => {
  const grid = buildDayData(date, events)
  const day = grid.days[0]

  return (
    <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div
        className={`flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3 ${
          day.isToday ? 'bg-secondary-light/20' : 'bg-slate-50'
        }`}
      >
        <div>
          <p className={`text-sm font-semibold capitalize ${day.isToday ? 'text-secondary-pure' : 'text-slate-700'}`}>
            {longDateLabel(date)}
          </p>
          <p className="text-xs text-slate-400">
            {day.allDayEvents.length + day.events.length === 0
              ? 'Nenhum evento neste dia'
              : `${day.allDayEvents.length + day.events.length} evento(s)`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => onCreate(date)}
          className="shrink-0 rounded-full bg-secondary-pure px-3 py-1.5 text-xs font-medium text-white"
        >
          + Criar
        </button>
      </div>

      {day.allDayEvents.length > 0 && (
        <div className="flex border-b border-slate-200 bg-slate-50/40">
          <div className="flex w-14 shrink-0 items-center justify-center text-[9px] uppercase text-slate-400">
            dia todo
          </div>
          <div className="flex-1 space-y-1 p-2">
            {day.allDayEvents.map(event => (
              <button
                key={`${event.id}-${event.date}`}
                type="button"
                onClick={() => onOpenEvent(event)}
                className={`block w-full truncate rounded px-2 py-1 text-left text-xs font-medium ${eventChipClass(
                  event.color,
                )}`}
              >
                {event.display_title}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex max-h-[70vh] overflow-y-auto">
        <div className="w-14 shrink-0">
          {grid.hours.map(hour => (
            <div key={hour} style={{ height: HOUR_HEIGHT }} className="relative">
              <span className="absolute -top-1.5 right-1.5 text-[10px] text-slate-400">{hour}</span>
            </div>
          ))}
        </div>

        <div
          onClick={() => onCreate(date)}
          style={{ height: grid.gridHeight }}
          className="relative flex-1 cursor-pointer border-l border-slate-200"
        >
          {grid.hours.map((hour, index) => (
            <div
              key={hour}
              className="absolute inset-x-0 border-b border-slate-100"
              style={{ top: index * HOUR_HEIGHT, height: HOUR_HEIGHT }}
            />
          ))}

          {day.events.map(({ event, top, height, leftPct, widthPct }) => (
            <button
              key={`${event.id}-${event.date}-${top}`}
              type="button"
              onClick={stopped(() => onOpenEvent(event))}
              style={{ top, height, left: `${leftPct}%`, width: `calc(${widthPct}% - 6px)` }}
              className={`absolute z-[1] overflow-hidden rounded px-2 py-1 text-left leading-tight shadow-sm ${eventChipClass(
                event.color,
              )}`}
            >
              <span className="block truncate text-xs font-semibold">{event.display_title}</span>
              <span className="block text-[10px] opacity-90">
                {event.starts_at}–{event.ends_at}
                {event.client_name ? ` · ${event.client_name}` : ''}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

/** A coluna abre o formulário; o evento em cima dela abre os detalhes. */
function stopped(action: () => void) {
  return (event: React.MouseEvent) => {
    event.stopPropagation()
    action()
  }
}

export default CalendarDayView
